import { useState, type ReactNode } from "react";
import { Box, Button, Grid2 as Grid, Typography, alpha, type Theme } from "@mui/material";
import { ReferenceField, TextField, type RaRecord } from "react-admin";
import type { EditorField } from "@/components/common/EditableGrid";
import { InPlaceSectionForm } from "./InPlaceSectionForm";
import { SectionCard } from "@/components/common/SectionCard";
import {
  SUBSCRIBER_RECORD_REASON,
  fieldLabel,
  formatFieldValue,
  getByPath,
  groupFields,
  isSet,
  isShown,
  type FieldGroup,
} from "./detailsFields";

/** Inherit colour, primary on hover, never bold (#7581 AC8 convention). */
export const quietLink = {
  color: "inherit",
  fontWeight: "inherit",
  textDecoration: "underline",
  textDecorationColor: (t: Theme) => alpha(t.palette.text.primary, 0.25),
  "&:hover": { color: "primary.main", textDecorationColor: "currentColor" },
} as const;

const EMPTY = "—";

/** One label/value row; the value is right of a muted label. */
export const Fact = ({ label, children, suffix }: { label: string; children: ReactNode; suffix?: string }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 2,
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ width: "45%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" fontWeight={500} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {children}
        {suffix && (
          <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
            {" "}
            · {suffix}
          </Typography>
        )}
      </Typography>
    </Box>
  </Grid>
);

/** Read-mode value for one field: a reference is fetched, everything else formatted. */
export const FieldValue = ({ field, value }: { field: EditorField; value: unknown }) => {
  if (!isSet(value)) return <>{EMPTY}</>;
  if (field.type === "reference" && field.reference) {
    return (
      <ReferenceField source={field.source} reference={field.reference} link="show" sx={{ "& a": quietLink }}>
        <TextField source={field.displayField ?? "id"} />
      </ReferenceField>
    );
  }
  const text = formatFieldValue(field, value);
  if (Array.isArray(text)) {
    return (
      <>
        {text.map((line, i) => (
          <Box key={i}>{line}</Box>
        ))}
      </>
    );
  }
  return <>{text}</>;
};

/**
 * Facts for one group. Empty values show as "—" (the group has at least one
 * value, or the user asked to see empty groups); `showWhen` hides as the grid does.
 */
export type ValueOverrides = Record<string, (record: unknown) => ReactNode>;

export const GroupFacts = ({
  group,
  record,
  valueOverrides,
}: {
  group: FieldGroup;
  record: unknown;
  valueOverrides?: ValueOverrides;
}) => (
  <>
    {group.fields.map((field) => {
      if (!isShown(field, record)) return null;
      const value = getByPath(record, field.source);
      const override = valueOverrides?.[field.source];
      return (
        <Fact
          key={field.source}
          label={fieldLabel(field)}
          suffix={field.readOnlyReason === SUBSCRIBER_RECORD_REASON ? SUBSCRIBER_RECORD_REASON : undefined}
        >
          {override ? override(record) : <FieldValue field={field} value={value} />}
        </Fact>
      );
    })}
  </>
);

const GroupCaption = ({ children }: { children: ReactNode }) => (
  <Grid size={12}>
    <Typography
      variant="caption"
      component="div"
      sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", mt: 1 }}
    >
      {children}
    </Typography>
  </Grid>
);

interface DetailsSectionProps {
  title: string;
  /** Lower-case name used in the button's aria-label ("Edit identity"). */
  name: string;
  /** Fields for the grid in edit mode. */
  fields: EditorField[];
  /** Fields for the facts in read mode; defaults to `fields`. */
  readFields?: EditorField[];
  /** Per-source read-mode value renderers (e.g. birth date with age). */
  valueOverrides?: ValueOverrides;
  record: unknown;
  canEdit: boolean;
  /** Copy rendered above the facts in both modes (member identifiers). */
  note?: string;
  /** Offer a "Show N more groups" toggle that reveals groups with no recorded value (eligibility). */
  collapseEmptyGroups?: boolean;
  /** Shown instead of facts when no group has a value. */
  emptyText?: string;
  /** Extra header actions, rendered left of Edit. */
  extraActions?: ReactNode;
}

/**
 * A Details-tab section: read-first facts in a SectionCard with an Edit button
 * that turns the facts into the same rows with inputs and a Save / Cancel foot
 * (InPlaceSectionForm). Independent of the other sections. The Overview grid
 * is no longer mounted here (Dillon, 2026-10-28: phase it out).
 */
export const DetailsSection = ({
  title,
  name,
  fields,
  readFields = fields,
  valueOverrides,
  record,
  canEdit,
  note,
  collapseEmptyGroups = false,
  emptyText,
  extraActions,
}: DetailsSectionProps) => {
  const [editing, setEditing] = useState(false);
  const [showEmptyGroups, setShowEmptyGroups] = useState(false);

  // Read mode shows only groups with a recorded value; inside one, empties read
  // "—". The toggle (eligibility) reveals the rest as all-"—" groups.
  const groups = groupFields(readFields, record);
  const emptyGroups = collapseEmptyGroups ? groups.filter((g) => !g.hasValue) : [];
  const visibleGroups = showEmptyGroups ? groups : groups.filter((g) => g.hasValue);
  const showCaptions = groups.length > 1;

  const toggle = !editing && emptyGroups.length > 0 && (
    <Button
      size="small"
      variant="text"
      onClick={() => setShowEmptyGroups((v) => !v)}
      sx={{ ...quietLink, textDecoration: "none", textTransform: "none", minWidth: 0, color: "text.secondary" }}
    >
      {showEmptyGroups
        ? "Hide empty groups"
        : `Show ${emptyGroups.length} more ${emptyGroups.length === 1 ? "group" : "groups"}`}
    </Button>
  );

  const action = (canEdit || toggle || extraActions) && (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {extraActions}
      {canEdit && !editing && (
        <Button
          size="small"
          variant="text"
          aria-label={`Edit ${name}`}
          onClick={() => setEditing(true)}
          sx={{ textTransform: "none", minWidth: 0 }}
        >
          Edit
        </Button>
      )}
      {toggle && canEdit && (
        <Typography variant="body2" color="text.secondary" aria-hidden>
          ·
        </Typography>
      )}
      {toggle}
    </Box>
  );

  return (
    <SectionCard title={title} action={action || undefined}>
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {note}
        </Typography>
      )}
      {editing ? (
        <InPlaceSectionForm fields={fields} record={record as RaRecord} name={name} onDone={() => setEditing(false)} />
      ) : visibleGroups.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText ?? "Nothing recorded."}
        </Typography>
      ) : (
        <Grid container columnSpacing={3}>
          {visibleGroups.map((group, i) => (
            <Grid container size={12} columnSpacing={3} key={group.title ?? `group-${i}`}>
              {showCaptions && group.title && <GroupCaption>{group.title}</GroupCaption>}
              <GroupFacts group={group} record={record} valueOverrides={valueOverrides} />
            </Grid>
          ))}
        </Grid>
      )}
    </SectionCard>
  );
};
